import { Button, Card, Input } from "@repo/ui";
import React from "react";
import { Link, useSearchParams } from "react-router-dom";

type NewsItem = {
  id: number;
  slug: string;
  title: string;
  description: string;
  cover_image: string | null;
  created_at: string;
  created_by: string | null;
  category_id: number | null;
};

type NewsCategory = {
  id: number;
  name: string;
  slug: string;
};

export function NewsList() {
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") || "";
  const cat = sp.get("cat") || "";
  const [list, setList] = React.useState<NewsItem[]>([]);
  const [categories, setCategories] = React.useState<NewsCategory[]>([]);

  React.useEffect(() => {
    (async () => {
      const [newsRes, catRes] = await Promise.all([
        fetch(`/api/news${cat ? `?cat=${encodeURIComponent(cat)}` : ""}`),
        fetch("/api/news-categories"),
      ]);
      const newsJson = (await newsRes.json()) as { status: string; data: NewsItem[] };
      const catJson = (await catRes.json()) as { status: string; data: NewsCategory[] };
      setList(newsJson.data || []);
      setCategories(catJson.data || []);
    })();
  }, [cat]);

  const filtered = list.filter(
    (n) =>
      (n.title || "").toLowerCase().includes(q.toLowerCase()) ||
      (n.description || "").toLowerCase().includes(q.toLowerCase())
  );

  function setCategory(next: string | null) {
    if (next) {
      sp.set("cat", next);
    } else {
      sp.delete("cat");
    }
    setSp(sp, { replace: true });
  }

  function setQuery(next: string) {
    if (next) {
      sp.set("q", next);
    } else {
      sp.delete("q");
    }
    setSp(sp, { replace: true });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">ข่าวสาร</h1>
          <p className="text-sm text-foreground/70">
            อัปเดตข่าวดวง ข่าวมงคล และกิจกรรมล่าสุดจาก Lucky News
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Input
            className="w-48 md:w-64"
            placeholder="ค้นหา..."
            value={q}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          type="button"
          size="sm"
          variant={cat ? "outline" : "default"}
          onClick={() => setCategory(null)}
        >
          ทั้งหมด
        </Button>
        {categories.map((c) => (
          <Button
            key={c.id}
            type="button"
            size="sm"
            variant={cat === c.slug ? "default" : "outline"}
            onClick={() => setCategory(c.slug)}
          >
            {c.name}
          </Button>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {filtered.map((n) => (
          <Card key={n.id} className="overflow-hidden bg-card/60 hover:bg-card transition-colors">
            {n.cover_image && (
              <div
                className="aspect-[16/9] bg-cover bg-center"
                style={{ backgroundImage: `url(${n.cover_image})` }}
              />
            )}
            <div className="p-4">
              <div className="text-xs text-foreground/60">
                {new Date(n.created_at).toLocaleDateString("th-TH")}
                {n.created_by ? ` · โดย ${n.created_by}` : ""}
              </div>
              <Link
                to={`/news/${n.slug}`}
                className="block mt-2 font-display text-lg hover:text-primary"
              >
                {n.title}
              </Link>
              <p className="text-sm text-foreground/70 mt-1 line-clamp-3">{n.description}</p>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-foreground/60 text-sm">
            ยังไม่มีข่าวในหมวดนี้
          </div>
        )}
      </div>
    </div>
  );
}

export default NewsList;
